import type { ApiClient } from '../api';
import { money } from '../format';
import { usePoll } from '../hooks';
import type { InPersonEvent } from '../types';

/**
 * In-person events (Matthew, 18 October 2026: "have all the correct data
 * from yesterday visible on the A.C.C.R.A platform"). Each event shows its
 * latest report from the IRL platform: hourly while it trades, then a
 * corrected final one. Earlier reports are kept and counted as superseded,
 * so a figure that changed overnight says so rather than silently moving.
 * Nothing renders until there is an event in the last 30 days.
 */

const londonTime = (iso: string, withDay = false) =>
  new Date(iso).toLocaleString('en-GB', {
    ...(withDay ? { weekday: 'short', day: 'numeric', month: 'short' } : {}),
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/London',
  });

export function InPersonEvents({ api }: { api: ApiClient }) {
  const res = usePoll<{ events: InPersonEvent[] }>(() => api.get('/api/in-person'), 60_000, ['in-person']);
  const events = res.data?.events ?? [];
  if (events.length === 0) return null;
  return (
    <section className="cc-inperson" aria-label="In-person events">
      <h2 className="section-title">In-person events</h2>
      {events.map((e) => (
        <EventCard key={e.slug} e={e} />
      ))}
    </section>
  );
}

function EventCard({ e }: { e: InPersonEvent }) {
  const c = e.counts;
  const m = (minor: number | null | undefined) => (minor === null || minor === undefined || !e.currency ? null : money(minor, e.currency));
  const pool = c.cashback_pool_minor;
  const paid = c.cashback_paid_minor;
  const stats: [string, string | null][] = [
    ['Sales', c.sales !== undefined ? String(c.sales) : null],
    ['Spend', m(e.spend_minor)],
    ['Cashback paid', paid !== undefined ? `${m(paid)}${pool ? ` of ${m(pool)}` : ''}` : null],
    ['Shoppers who bought', c.shoppers !== undefined ? String(c.shoppers) : null],
    ['Passes issued', c.passes_issued !== undefined ? String(c.passes_issued) : null],
    ['Saved to CashBlack', c.passes_saved !== undefined ? String(c.passes_saved) : null],
    [
      'Stalls',
      c.stalls_total !== undefined
        ? [
            c.stalls_traded !== undefined ? `${c.stalls_traded} traded` : c.stalls_trading !== undefined ? `${c.stalls_trading} trading` : null,
            c.stalls_signed_in !== undefined ? `${c.stalls_signed_in} signed in` : null,
            `${c.stalls_total} listed`,
          ]
            .filter(Boolean)
            .join(' · ')
        : null,
    ],
    ['Peak hour', e.peak_hour],
    ['First and last sale', e.first_sale_at && e.last_sale_at ? `${londonTime(e.first_sale_at)} to ${londonTime(e.last_sale_at)}` : null],
  ];
  const badge = e.final ? 'Final' : e.status === 'closed' ? 'Closed' : 'Live';
  return (
    <div className="card cc-inperson-card">
      <div className="card-head">
        <span className="card-title">
          {e.name}
          {e.venue ? <span className="card-note"> · {e.venue}</span> : null}
        </span>
        <span className={`cc-inperson-badge ${badge.toLowerCase()}`}>{badge}</span>
      </div>
      <dl className="cc-inperson-stats">
        {stats
          .filter(([, v]) => v)
          .map(([k, v]) => (
            <div key={k}>
              <dt>{k}</dt>
              <dd>{v}</dd>
            </div>
          ))}
      </dl>
      {e.top_stalls.length > 0 && (
        <p className="cc-inperson-line">
          <strong>Top stalls:</strong>{' '}
          {e.top_stalls.map((t) => (t.spend_minor !== null ? `${t.name} ${m(t.spend_minor)}` : t.name)).join(' · ')}
        </p>
      )}
      {e.categories.length > 0 && (
        <p className="cc-inperson-line">
          <strong>By category:</strong> {e.categories.map((k) => `${k.name} ${k.share_pct}%`).join(' · ')}
        </p>
      )}
      <p className="card-note">
        As of {londonTime(e.as_of, true)}
        {e.superseded > 0 ? ` · replaces ${e.superseded} earlier report${e.superseded === 1 ? '' : 's'}` : ''}
      </p>
    </div>
  );
}
